import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, XCircle, EnvelopeSimple } from "@phosphor-icons/react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("verifying"); // verifying | success | error | missing
  const [email, setEmail] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const firedRef = useRef(false);

  useEffect(() => {
    if (!token || token.length < 16) { setState("missing"); return; }
    // Guard against React.StrictMode double-invoke — the token is single-use,
    // so we must only POST once even when the effect runs twice in dev.
    if (firedRef.current) return;
    firedRef.current = true;
    (async () => {
      try {
        const r = await api.post("/auth/verify-email", { token });
        setEmail(r.data.email);
        setState("success");
      } catch (err) {
        setErrorMsg(formatApiError(err.response?.data?.detail, "Verification failed"));
        setState("error");
      }
    })();
  }, [token]);

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success("A fresh verification link has been sent.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Please sign in first, then resend."));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display text-2xl font-extrabold text-slate-900 block mb-8">
          Shift<span className="text-blue-600">Flex</span>
        </Link>
        <div className="bg-white border border-slate-200 rounded-md p-8 text-center" data-testid={`verify-state-${state}`}>
          {state === "verifying" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4 animate-pulse">
                <EnvelopeSimple size={28} className="text-slate-500" />
              </div>
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Verifying your email…</h2>
            </>
          )}
          {state === "success" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <CheckCircle size={32} weight="fill" className="text-emerald-600" />
              </div>
              <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-2">Email verified!</h2>
              <p className="text-slate-600">
                {email ? <><b>{email}</b> is confirmed.</> : "Your email is confirmed."} You can now book and post shifts.
              </p>
              <Link to="/login"><Button className="mt-6 bg-blue-600 hover:bg-blue-700 h-11" data-testid="verify-continue-btn">Continue to sign in</Button></Link>
            </>
          )}
          {state === "error" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
                <XCircle size={32} weight="fill" className="text-red-600" />
              </div>
              <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-2">Couldn't verify</h2>
              <p className="text-slate-600 mb-4">{errorMsg}</p>
              <Button onClick={resend} variant="outline" data-testid="verify-resend-btn">Resend verification email</Button>
            </>
          )}
          {state === "missing" && (
            <>
              <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-2">Invalid verification link</h2>
              <p className="text-slate-600 mb-4">The link is missing a token. Sign in and hit "Resend" to get a fresh one.</p>
              <Link to="/login"><Button className="bg-blue-600 hover:bg-blue-700 h-11">Back to sign in</Button></Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
